import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";

const ALL = "all";

function FilterSelect({ value, onChange, options, testId, label }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[170px]" data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}: tutti</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function StudentChecklist({ students = [], values = [], onChange, defaultYear = ALL }) {
  const [q, setQ] = useState("");
  const [year, setYear] = useState(defaultYear);
  const [ay, setAy] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const sel = new Set(values);

  const ayOptions = useMemo(() => [...new Set(students.map((s) => s.academic_year).filter(Boolean))].sort().map((a) => [a, a]), [students]);
  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return students.filter((s) => (year === ALL || String(s.year_level) === year)
      && (ay === ALL || s.academic_year === ay)
      && (status === ALL || (s.status || "attivo") === status)
      && (!n || `${s.first_name} ${s.last_name} ${s.matricola || ""}`.toLowerCase().includes(n)))
      .sort((a, b) => (a.last_name || "").localeCompare(b.last_name || ""));
  }, [students, q, year, ay, status]);
  const allOn = shown.length > 0 && shown.every((s) => sel.has(s.id));
  const toggleAll = () => onChange(allOn ? values.filter((id) => !shown.some((s) => s.id === id))
    : [...new Set([...values, ...shown.map((s) => s.id)])]);
  const toggle = (id) => onChange(sel.has(id) ? values.filter((v) => v !== id) : [...values, id]);

  return (
    <div className="space-y-3" data-testid="storico-student-checklist">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-stone-400" />
          <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome o matricola" data-testid="storico-students-search" />
        </div>
        <FilterSelect value={year} onChange={setYear} label="Anno di corso" testId="storico-filter-year"
          options={[["1", "1° anno"], ["2", "2° anno"], ["3", "3° anno"]]} />
        <FilterSelect value={ay} onChange={setAy} label="Anno accademico" testId="storico-filter-ay" options={ayOptions} />
        <FilterSelect value={status} onChange={setStatus} label="Stato" testId="storico-filter-status"
          options={[["attivo", "Attivi"], ["ritirato", "Ritirati"], ["diplomato", "Diplomati"], ["sospeso", "Sospesi"]]} />
        <Button variant="outline" size="sm" onClick={toggleAll} disabled={!shown.length} data-testid="storico-select-all">
          {allOn ? "Deseleziona tutti" : `Seleziona tutti (${shown.length})`}
        </Button>
      </div>
      <div className="border border-stone-200 rounded-md max-h-72 overflow-y-auto divide-y divide-stone-100">
        {shown.map((s) => (
          <label key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-stone-50 cursor-pointer" data-testid={`storico-student-row-${s.id}`}>
            <Checkbox checked={sel.has(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`storico-student-check-${s.id}`} />
            <span className="flex-1">{s.last_name} {s.first_name}</span>
            <span className="text-xs text-stone-500">{s.matricola || ""}{s.year_level ? ` · ${s.year_level}° anno` : ""}{s.academic_year ? ` · ${s.academic_year}` : ""}</span>
          </label>
        ))}
        {!shown.length && <p className="p-4 text-sm text-stone-500 text-center" data-testid="storico-students-empty">Nessuno studente residenziale con questi filtri.</p>}
      </div>
    </div>
  );
}
